import React, { useState, useCallback, useEffect } from 'react';
import { View, StyleSheet, Pressable, useWindowDimensions, FlatList, Platform } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '@react-navigation/native';
import { ActivityIndicator, Card } from 'react-native-paper';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomButton from '../../../components/CustomButton';
import { useTranslation } from 'react-i18next';
import { showMessage } from 'react-native-flash-message';
import ExcelColumnsComponent from './MassUploadComponents/ExcelColumnsComponent';
import TableComponent from './MassUploadComponents/TableComponent';
import databaseTables from './MassUploadComponents/DatabaseTables';
import Swal from 'sweetalert2';
import { useCompany } from '../../../hooks/useCompany';
import { v4 as uuidv4 } from 'uuid';

const getTranslationFile = (language) => {
    switch (language) {
        case 'en':
            return require('../../../translations/en/databaseTableMappings')?.default;
        case 'gr':
            return require('../../../translations/gr/databaseTableMappings')?.default;
        default:
            return require('../../../translations/en/databaseTableMappings')?.default;
    }
};

const HRAdminMassUploadScreen = () => {
    const { colors } = useTheme();
    const { width, height } = useWindowDimensions();
    const { t, i18n } = useTranslation();
    const databaseTableMappings = getTranslationFile(i18n.language);
    const styles = makeStyles(colors, width, height);
    const [selectedTable, setSelectedTable] = useState({
        tableName: '',
        description: ''
    });
    const [importedExcelData, setImportedExcelData] = useState({ base64data: null, name: '' });
    const [tableColInfo, setTableColInfo] = useState({});
    const [exportedExcelData, setExportedExcelData] = useState({});
    const [tableViewData, setTableViewData] = useState(null);
    const [selectedValues, setSelectedValues] = useState({});
    const [loading, setIsLoading] = useState(false);
    const [currentScreen, setCurrentScreen] = useState('MassUpload');
    const [progress, setProgress] = useState(0); // progress bar for when uploading data
    const [isUploading, setIsUploading] = useState(false);
    const { uploadBatch, generateExcelData, dropdownValues } = useCompany();

    let XLSX = require('xlsx');

    //#region UseEffect 
    useEffect(() => {
        const fetchData = async () => {
            await handlePressTable(databaseTables[0].tableName, databaseTables[0].description)
        };
        fetchData();
    }, []);

    useEffect(() => {
        setExportedExcelData(prevState => ({
            ...prevState,
            selectedValue: selectedValues
        }));
    }, [selectedValues]);

    useEffect(() => {
        if (importedExcelData?.base64data) {
            ViewExcel();
        }
    }, [importedExcelData]);
    //#endregion

    //#region Import Excel 
    // Step 1: Get Excel Document from User
    const handleFileUpload = useCallback(async () => {
        try {
            console.log('first')
            const result = await DocumentPicker.getDocumentAsync({
                type: [
                    "application/vnd.ms-excel",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                ],
                copyToCacheDirectory: false,
            });
            if (!result.canceled) {
                const base64Data = result?.assets[0]?.uri;
                let data = {
                    base64data: base64Data,
                    name: result?.assets[0]?.name
                }
                setImportedExcelData(data);
            }
        } catch (error) {
            console.error('Error picking document:', error);
        }
    }, []);

    // Step 2: Read Excel Document 
    const readExcelFile = async (base64Uri, isUpload = false) => {
        try {
            const binaryString = (() => {
                const base64Data = base64Uri.base64data;
                if (base64Data.includes(';base64,')) {
                    return atob(base64Data.split(';base64,')[1]);
                } else {
                    return atob(base64Data);
                }
            })();
            // Convert binary string to array buffer
            const arrayBuffer = new ArrayBuffer(binaryString.length);
            const uint8Array = new Uint8Array(arrayBuffer);
            for (let i = 0; i < binaryString.length; i++) {
                uint8Array[i] = binaryString.charCodeAt(i);
            }
            // Read the Excel file using XLSX
            const workbook = XLSX.read(uint8Array, { type: 'array' });
            // Get the first sheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            // Convert the sheet to JSON
            const jsonOpts = {
                header: 1, // Treat the first row as headers
                blankrows: false,
                raw: false,
                dateNF: 'd"/"m"/"yyyy' // Accept dates
            };
            const data = XLSX.utils.sheet_to_json(worksheet, jsonOpts);
            const headers = data[0];
            const result = [];
            for (let i = 1; i < data.length; i++) {
                const row = data[i];
                const rowObject = {};
                for (let j = 0; j < headers.length; j++) {
                    const value = row[j];
                    rowObject[headers[j]] = (value === '' || value === undefined) ? undefined : convertDateFormat(value);
                }
                result.push(rowObject);
            }
            if (isUpload) {
                let columnNames = mapDisplayNamesToActualColumnNames(result, databaseTableMappings);
                setIsUploading(true);
                let response = await uploadDataInBatches(columnNames)
                if (response.status === 200) {
                    showMessage({
                        message: `${t('common:succImportedExcel')}: ${response.rowsInserted}`,
                        type: "success",
                    });
                }
                else {
                    showMessage({
                        message: t('common:unsuccImportedExcel'),
                        type: "danger",
                    });
                }
                setIsUploading(false);
            }
            else
                return result;
        } catch (error) {
            console.error('Error reading Excel file:', error);
        }
    };


    // Step 3: Divide the rows into batches
    const uploadDataInBatches = async (data) => {
        const batchSize = 150;
        let response = null;
        let rowsInserted = 0;
        setProgress(0);
        for (let i = 0; i < data.length; i += batchSize) {
            const batch = data.slice(i, i + batchSize);
            response = await uploadBatch(removeRowNumKey(batch), selectedTable);
            rowsInserted += response ? response?.data?.rowsInserted : 0;
            const progress = Math.min(100, Math.round(((i + batchSize) / data.length) * 100));
            setProgress(progress);
        }
        return { status: response.status, rowsInserted: rowsInserted };
    };
    //#endregion

    //#region View Excel
    const ReadExcelData = async () => {
        return await readExcelFile(importedExcelData, isUpload = false);
    }
    const ViewExcel = async () => {
        try {
            setCurrentScreen('ViewExcel')
            ReadExcelData().then(data => {
                data = data.map((item, index) => ({
                    ...item,
                    index: uuidv4()
                }));
                setTableViewData({
                    data: data
                })
            });
        } catch (error) {
            console.error('Error viewing Excel:', error);
        }
    };
    //#endregion

    //#region Export Excel
    // Create Excel
    const getExcelColumns = (data = undefined, dropdowns) => {
        const wb = XLSX.utils.book_new();
        const wsData = getColumnNames(data);
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        const excelBinary = XLSX.write(wb, { type: 'binary', bookType: 'xlsx' });
        const item = {
            data: btoa(excelBinary),
            name: data ? `${data.name}` : `${exportedExcelData.data.name}`,
            type: 'xlsx'
        };
        if (data) {
            let html = {
                data: data,
                dropdowns: dropdowns
            };
            setTableColInfo(html);
        } else {
            downloadExcelFile(item);
        }
    };

    const getColumnNames = (data = undefined) => {
        if (data && data.data.length > 0) {
            const columnNames = data.data.map(column => column.COLUMN_NAME);
            const dataRows = data.data.map(column => {
                return {
                    COLUMN_NAME: column.COLUMN_NAME,
                    ORDINAL_POSITION: column.ORDINAL_POSITION,
                    DATA_TYPE: column.DATA_TYPE,
                    CHARACTER_MAXIMUM_LENGTH: column.CHARACTER_MAXIMUM_LENGTH
                };
            });
            return [columnNames, ...dataRows.map(row => Object.values(row))];
        }
        const columnNames = exportedExcelData.data.data.map(column => t(`databaseTableMappings:${column.COLUMN_NAME}`, { returnObjects: true })?.displayName || column.COLUMN_NAME);
        const dataRows = exportedExcelData.data.data.map(column => {
            return exportedExcelData?.selectedValue?.hasOwnProperty(column.COLUMN_NAME) ? exportedExcelData?.selectedValue[column.COLUMN_NAME] : '';
        });
        return [columnNames, dataRows];
    }

    const downloadExcelFile = useCallback(async (item) => {
        if (Platform.OS === 'web') {
            // create blob
            const blobData = await fetch(`data:plain/text;base64,${item.data}`).then(res => {
                return res.blob();
            });
            // create download link
            const url = URL.createObjectURL(blobData);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `${item.name}.${item.type}`,
            );
            // insert link on page
            document.body.appendChild(link);
            // start download
            link.click();
            // remove link
            link.parentNode.removeChild(link);
        }
    }, []);

    //#endregion

    //#region Handlers
    async function handlePressTable(tableName, description) {
        setSelectedTable(prevSelectedTable =>
            prevSelectedTable && prevSelectedTable.tableName === tableName
                ? prevSelectedTable
                : { tableName, description }
        );
        setIsLoading(true);
        generateExcelData(tableName, description).then(async (response) => {
            let data = {
                data: response.data,
                name: description
            };
            let dropdowns = await dropdownValues(tableName);
            setExportedExcelData({
                data: data,
                selectedValue: null,
                hasDropdownValues: dropdowns ? Object.keys(dropdowns).length > 0 : false
            });
            getExcelColumns(data, dropdowns);
        })
        setIsLoading(false);
    };

    const handleValueChange = (columnName, value) => {
        setSelectedValues(prevState => ({ ...prevState, [columnName]: value }));
    };
    //#endregion

    //#region Helpers
    const removeRowNumKey = (array) => {
        return array.map(obj => {
            const { __rowNum__, ...rest } = obj; // Destructure to exclude __rowNum__
            return rest; // Return the object without __rowNum__
        });
    };

    const clearImportedData = () => {
        setImportedExcelData({ base64data: null, name: '' });
    }

    const convertDateFormat = (dateString) => {
        const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
        const match = dateString.match(dateRegex);
        if (match) {
            // Extract day, month, and year
            const day = match[1].padStart(2, '0');
            const month = match[2].padStart(2, '0');
            const year = match[3];
            return `${month}/${day}/${year}`;
        }
        return dateString;
    };

    const mapDisplayNamesToActualColumnNames = (displayNames, fields) => {
        let transformed = [];

        const initialLanguage = i18n.language;
        const languages = [initialLanguage, 'en', 'gr'];

        for (const language of languages) {
            const databaseTableMappings = getTranslationFile(language);

            transformed = displayNames
                .map(item => {
                    const newObj = {};
                    for (const displayKey in item) {
                        const match = Object.keys(databaseTableMappings)
                            .find(
                                key => databaseTableMappings[key].displayName.toLowerCase() === displayKey.toLowerCase()
                            );
                        if (match) newObj[match] = item[displayKey];
                    }
                    return newObj;
                });
            if (Object.keys(transformed[0]).length > 0) {
                break;
            }
        }
        return transformed;
    };
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            {tableViewData && currentScreen === 'ViewExcel' ? (
                <>
                    <TableComponent
                        importedData={tableViewData}
                        setScreen={setCurrentScreen}
                        handleFileUpload={readExcelFile}
                        clearImportedData={clearImportedData}
                        mapDisplayNamesToActualColumnNames={mapDisplayNamesToActualColumnNames}
                        dropdowns={tableColInfo.dropdowns}
                        dataConstraints={tableColInfo.data}
                        isUploading={isUploading}
                        progress={progress}
                        name={importedExcelData.name}
                    />
                </>
            ) : (
                <>
                    <SafeAreaView style={styles.row}>
                        {/* left column */}
                        <SafeAreaView style={{ width: '30%' }}>
                            <CustomLabel
                                value={t('common:dataDestinationUpload')}
                                type='MAJOR'
                            />
                            <FlatList
                                data={databaseTables}
                                renderItem={({ item }) => (
                                    <Card style={selectedTable?.tableName === item.tableName ? [styles.border, styles.cardstyle] : [styles.cardstyle]} >
                                        <Pressable onPress={() => handlePressTable(item.tableName, item.description)}>
                                            <CustomLabel
                                                value={item.description}
                                                type='MAJOR' />
                                        </Pressable>
                                    </Card>
                                )}
                                keyExtractor={item => item.tableName}
                            />
                        </SafeAreaView>
                        {/* right column */}
                        <SafeAreaView style={{ width: '70%' }}>
                            <SafeAreaView style={{ height: '100%' }}>
                                <SafeAreaView style={{ height: '92%' }}>
                                    {Object.keys(tableColInfo).length > 0 ? (
                                        <SafeAreaView style={{ height: '100%' }}>
                                            <ExcelColumnsComponent
                                                dropdowns={tableColInfo.dropdowns}
                                                columnData={tableColInfo.data}
                                                onValueChange={handleValueChange}
                                            />
                                        </SafeAreaView>
                                    ) : (
                                        <SafeAreaView style={styles.centered}>
                                            {loading ? (
                                                <View style={styles.loader}>
                                                    <ActivityIndicator animating={true} color={colors.blue} />
                                                </View>
                                            ) : (
                                                <CustomLabel
                                                    value={t('common:downloadAsExcel')}
                                                    type='MAJOR'
                                                />
                                            )}
                                        </SafeAreaView>
                                    )}
                                </SafeAreaView>

                            </SafeAreaView>
                            <SafeAreaView style={{ alignItems: 'flex-end' }}>
                                <SafeAreaView style={{ width: '50%' }}>
                                    <SafeAreaView style={styles.buttonRight}>
                                        <CustomButton
                                            text={t('common:downloadAsExcel')}
                                            onPress={() => getExcelColumns()}
                                            type="BUTTON_SAVE"
                                            disabled={
                                                Object.keys(exportedExcelData).length === 0 ||
                                                (exportedExcelData?.hasDropdownValues && exportedExcelData?.selectedValue === null)
                                            }
                                            iconName="download-outline"
                                            show="BUTTON_SAVE"
                                            textTop="0"
                                            sz={20}
                                        />
                                        <CustomButton
                                            text={t('common:importExcel')}
                                            onPress={async () => {
                                                Swal.fire({
                                                    title: `<text style="font-family:'Roboto-Medium', font-size:16px">${t('common:destinationTableMess')}</text>`,
                                                    icon: "warning",
                                                    showCancelButton: true,
                                                    reverseButtons: true,
                                                    cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t('common:cancel')}</text>`,
                                                    cancelButtonColor: '#d3d3d3',
                                                    confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t('common:confirmMessage')}</text>`,
                                                    confirmButtonColor: '#A0A2FF',
                                                }).then(async (result) => {
                                                    if (result.isConfirmed) {
                                                        await handleFileUpload();
                                                    };
                                                });
                                            }}
                                            type="BUTTON_SAVE"
                                            iconName="add-circle-outline"
                                            show="BUTTON_SAVE"
                                            textTop="0"
                                            sz={20}
                                        />
                                    </SafeAreaView>
                                </SafeAreaView>
                            </SafeAreaView>
                        </SafeAreaView>

                    </SafeAreaView>
                </>
            )}
        </SafeAreaView>
    );
};

const makeStyles = (colors, width, height) => StyleSheet.create({
    item: {
        fontSize: 16,
        marginBottom: 8,
    },
    mainView: {
        flex: 1,
        marginLeft: '3%',
        marginRight: '3%',
        marginBottom: '3%',
        height: '100%',
    },
    row: {
        flex: 1,
        flexDirection: "row",
    },
    column: {
        width: '50%',
    },
    cardstyle: {
        paddingLeft: 10,
        paddingRight: 10,
        margin: 10,
        borderRadius: 5,
        elevation: 3,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        flex: 1
    },
    border: {
        borderColor: colors.buttons_primary,
        borderWidth: 1,
    },
    buttonRight: {
        alignSelf: 'flex-end',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '75%',
    },
    centered: {
        justifyContent: 'center',
        alignSelf: 'center'
    },
});

export default HRAdminMassUploadScreen;